// src/hooks/useBreakpoint.tsx
import { useEffect, useMemo, useRef, useState } from "react";
function sanitizeMq(mq) {
  return mq.replace("@media", "").trim();
}
function getMediaQueryList(mediaQuery) {
  return typeof window !== "undefined" ? window.matchMedia(mediaQuery) : null;
}
function useBreakpoint(mediaQuery) {
  const sanitizedMediaQuery = useMemo(
    () => sanitizeMq(mediaQuery),
    [mediaQuery]
  );
  const mediaQueryListRef = useRef(getMediaQueryList(sanitizedMediaQuery));
  const [isActive, setIsActive] = useState(
    () => {
      var _a;
      return Boolean((_a = mediaQueryListRef.current) == null ? void 0 : _a.matches);
    }
  );
  useEffect(() => {
    const mediaQueryList = getMediaQueryList(sanitizedMediaQuery);
    mediaQueryListRef.current = mediaQueryList;
    setIsActive(Boolean(mediaQueryList == null ? void 0 : mediaQueryList.matches));
    const handleChange = (e) => {
      setIsActive(e.matches);
    };
    mediaQueryList == null ? void 0 : mediaQueryList.addListener(handleChange);
    return () => {
      mediaQueryList == null ? void 0 : mediaQueryList.removeListener(handleChange);
    };
  }, [sanitizedMediaQuery, setIsActive, mediaQueryListRef]);
  return isActive;
}

// src/hooks/useDimensions.ts
import { ResizeObserver } from "@juggle/resize-observer";
import useMeasure from "react-use-measure";

// src/helpers/assertNever.ts
var assertNever = (x, customErrorMessage) => {
  throw new Error(
    customErrorMessage ?? `Value should be unreachable. Received: ${x}`
  );
};

// src/helpers/motion.tsx
import { motion as Motion } from "framer-motion";
import { useContext } from "react";
import * as React from "react";
import { jsx } from "react/jsx-runtime";
var MotionContext = React.createContext({
  motion: null,
  MotionWrapper: React.Fragment
});
var MotionProvider = ({
  motion,
  wrapper,
  children
}) => /* @__PURE__ */ jsx(MotionContext.Provider, {
  value: { MotionWrapper: wrapper, motion },
  children
});
var errorMessage = "Please ensure this component is rendered within a @brexhq/metal-utils MotionProvider";
var useMotion = () => {
  const motionValue = useContext(MotionContext);
  if (!motionValue.motion) {
    throw new Error(errorMessage);
  }
  return motionValue;
};
var MockMotionProvider = ({ children }) => /* @__PURE__ */ jsx(MotionProvider, {
  motion: Motion,
  wrapper: React.Fragment,
  children
});

// src/helpers/isSsr.ts
var isSSR = typeof window === "undefined";

// src/helpers/index.ts
import { AnimatePresence } from "framer-motion";

// src/helpers/isChromatic.ts
var isChromatic = (windowArg) => {
  const windowToCheck = windowArg || typeof window !== "undefined" && window;
  return !!(windowToCheck && (windowToCheck.navigator.userAgent.match(/Chromatic/) || windowToCheck.location.href.match(/chromatic=true/)));
};

// src/helpers/wrappedCreatePortal.ts
import { createPortal } from "react-dom";

// src/helpers/numbers.ts
var range = (start, stop, step = 1) => Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

// src/helpers/noOp.ts
var noOp = () => {
};

// src/helpers/throttle.ts
var throttle = (callback, timeMs, {
  leading
} = {}) => {
  let isThrottling = false;
  let timeout;
  let argCache;
  return (...args) => {
    argCache = args;
    if (isThrottling)
      return;
    isThrottling = true;
    clearTimeout(timeout);
    if (leading) {
      callback(...args);
    }
    timeout = setTimeout(() => {
      callback(...argCache);
      isThrottling = false;
    }, timeMs);
  };
};

// src/helpers/getFirstAndLastInitials.ts
var removeParenthesesRegex = / *\([^)]*\) */g;
var getFirstAndLastInitials = (fullName) => {
  var _a;
  const name = fullName.replace(removeParenthesesRegex, "").split(" ");
  const firstInitial = ((_a = name[0]) == null ? void 0 : _a.charAt(0)) || "";
  const lastInitial = name.length > 1 && name[name.length - 1].charAt(0) || "";
  return `${firstInitial}${lastInitial}`.toUpperCase();
};

// src/hooks/useDimensions.ts
var useDimensions = ({
  debounceResizeDuration = 0,
  debounceScrollDuration = 0,
  updateOnScroll = false
} = {}) => {
  const [ref, dimensions] = useMeasure({
    debounce: {
      scroll: debounceScrollDuration,
      resize: debounceResizeDuration
    },
    scroll: updateOnScroll,
    polyfill: !isSSR && window.ResizeObserver || ResizeObserver
  });
  return { ref, dimensions };
};

// src/hooks/useInterval.tsx
import { useEffect as useEffect2, useRef as useRef2 } from "react";
var noop = () => {
};
var useInterval = (callback, delayInMs) => {
  const savedCallback = useRef2();
  useEffect2(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect2(() => {
    function tick() {
      var _a;
      (_a = savedCallback.current) == null ? void 0 : _a.call(savedCallback);
    }
    let id;
    if (delayInMs !== null) {
      id = setInterval(tick, delayInMs);
      return () => clearInterval(id);
    }
    return noop;
  }, [delayInMs]);
};

// src/hooks/useResponsiveValue.ts
import { breakpoints } from "@brexhq/metal-foundation";
import { useMemo as useMemo2 } from "react";
function useResponsiveValue(responsiveValue) {
  const phoneActive = useBreakpoint(breakpoints.phone);
  const tabletPortraitActive = useBreakpoint(breakpoints.tabletPortrait);
  const smallDesktopActive = useBreakpoint(breakpoints.smallDesktop);
  const tabletLandscapeActive = useBreakpoint(breakpoints.tabletLandscape);
  const desktopActive = useBreakpoint(breakpoints.desktop);
  return useMemo2(() => {
    const breakpointsInAscendingOrder = [
      ["phone", phoneActive],
      ["tabletPortrait", tabletPortraitActive],
      ["smallDesktop", smallDesktopActive],
      ["tabletLandscape", tabletLandscapeActive],
      ["desktop", desktopActive]
    ];
    for (let i = 0; i < breakpointsInAscendingOrder.length; i += 1) {
      const [breakpointKey, isBreakpointActive] = breakpointsInAscendingOrder[i];
      const value = responsiveValue[breakpointKey];
      if (isBreakpointActive && value !== void 0) {
        return value;
      }
    }
    return responsiveValue.default;
  }, [
    responsiveValue,
    phoneActive,
    tabletLandscapeActive,
    tabletPortraitActive,
    desktopActive,
    smallDesktopActive
  ]);
}

// src/hooks/useTimeout.ts
import { useEffect as useEffect3, useRef as useRef3 } from "react";
var noop2 = () => {
};
function useTimeout(callback, delayInMs) {
  const savedCallback = useRef3();
  useEffect3(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect3(() => {
    if (delayInMs === null)
      return noop2;
    const id = setTimeout(() => {
      var _a;
      return (_a = savedCallback.current) == null ? void 0 : _a.call(savedCallback);
    }, delayInMs);
    return () => clearTimeout(id);
  }, [delayInMs]);
}

// src/hooks/useBodyScrollLock.tsx
import * as React2 from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var useIsomorphicLayoutEffect = isSSR ? noOp : React2.useLayoutEffect;
var BodyScrollContext = React2.createContext(void 0);
var BodyScrollProvider = ({
  children
}) => {
  const [
    numComponentsRequestingLockedBodyScroll,
    setNumComponentsRequestingLockedBodyScroll
  ] = React2.useState(0);
  const handleRequestLockBodyScroll = React2.useCallback(() => {
    setNumComponentsRequestingLockedBodyScroll((num) => num + 1);
  }, [setNumComponentsRequestingLockedBodyScroll]);
  const handleRequestUnlockBodyScroll = React2.useCallback(() => {
    setNumComponentsRequestingLockedBodyScroll((num) => Math.max(num - 1, 0));
  }, [setNumComponentsRequestingLockedBodyScroll]);
  const isLocked = numComponentsRequestingLockedBodyScroll > 0;
  useIsomorphicLayoutEffect(() => {
    const previousOverflow = document.body.style.overflow;
    if (isLocked) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      if (isLocked) {
        document.body.style.overflow = previousOverflow;
      }
    };
  }, [isLocked]);
  const bodyScrollContext = React2.useMemo(
    () => ({
      isLocked,
      onRequestLockBodyScroll: handleRequestLockBodyScroll,
      onRequestUnlockBodyScroll: handleRequestUnlockBodyScroll
    }),
    [handleRequestLockBodyScroll, handleRequestUnlockBodyScroll, isLocked]
  );
  return /* @__PURE__ */ jsx2(BodyScrollContext.Provider, {
    value: bodyScrollContext,
    children
  });
};
function useBodyScrollLock(shouldLock = true) {
  const bodyScrollContext = React2.useContext(BodyScrollContext);
  if (typeof bodyScrollContext === "undefined") {
    throw new Error(
      "useBodyScrollLock must be used within a BodyScrollProvider"
    );
  }
  const { onRequestLockBodyScroll, onRequestUnlockBodyScroll } = bodyScrollContext;
  useIsomorphicLayoutEffect(() => {
    if (shouldLock) {
      onRequestLockBodyScroll();
    }
    return () => {
      if (shouldLock) {
        onRequestUnlockBodyScroll();
      }
    };
  }, [onRequestLockBodyScroll, onRequestUnlockBodyScroll, shouldLock]);
}
export {
  AnimatePresence,
  BodyScrollProvider,
  MockMotionProvider,
  MotionProvider,
  assertNever,
  createPortal,
  getFirstAndLastInitials,
  isChromatic,
  isSSR,
  noOp,
  range,
  sanitizeMq,
  throttle,
  useBodyScrollLock,
  useBreakpoint,
  useDimensions,
  useInterval,
  useMotion,
  useResponsiveValue,
  useTimeout
};
